"use client"

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react"
import { useRouter, usePathname } from "next/navigation"
import type { User, UserRole } from "./types"
import { api } from "./api"

interface AuthContextType {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  hasRole: (role: UserRole | UserRole[]) => boolean
  isEditor: boolean
  isManager: boolean
  sessionWarning: boolean
  extendSession: () => void
  timeRemaining: number
}

const AuthContext = createContext<AuthContextType | null>(null)

const SESSION_DURATION = 15 * 60 * 1000 // 15 minutes
const WARNING_THRESHOLD = 60 * 1000 // 1 minute before expiry

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const pathname = usePathname()
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [sessionWarning, setSessionWarning] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(0)
  const sessionTimerRef = useRef<NodeJS.Timeout | null>(null)
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null)
  const countdownRef = useRef<NodeJS.Timeout | null>(null)
  const expiryRef = useRef<number>(0)

  const clearTimers = useCallback(() => {
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current)
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
    if (countdownRef.current) clearInterval(countdownRef.current)
  }, [])

  const logout = useCallback(() => {
    clearTimers()
    setUser(null)
    setToken(null)
    setSessionWarning(false)
    localStorage.removeItem("cms_token")
    localStorage.removeItem("cms_user")
    localStorage.removeItem("cms_token_expiry")
    
    // Only redirect to login if user is on a dashboard page
    // Don't redirect if user is on website pages
    const isDashboardPage = pathname?.startsWith("/dashboard") || 
                           pathname?.startsWith("/articles") ||
                           pathname?.startsWith("/categories") ||
                           pathname?.startsWith("/tags") ||
                           pathname?.startsWith("/analytics") ||
                           pathname?.startsWith("/users") ||
                           pathname?.startsWith("/settings") ||
                           pathname?.startsWith("/website-settings") ||
                           pathname === "/login"
    
    if (isDashboardPage) {
      router.push("/login")
    }
    // If on website pages, just clear the auth state without redirecting
  }, [clearTimers, router, pathname])

  const startSessionTimer = useCallback(() => {
    clearTimers()
    const expiry = Date.now() + SESSION_DURATION
    expiryRef.current = expiry
    localStorage.setItem("cms_token_expiry", String(expiry))

    warningTimerRef.current = setTimeout(() => {
      setSessionWarning(true)
      setTimeRemaining(WARNING_THRESHOLD / 1000)
      countdownRef.current = setInterval(() => {
        const remaining = Math.max(0, Math.ceil((expiryRef.current - Date.now()) / 1000))
        setTimeRemaining(remaining)
        if (remaining <= 0) {
          logout()
        }
      }, 1000)
    }, SESSION_DURATION - WARNING_THRESHOLD)

    sessionTimerRef.current = setTimeout(logout, SESSION_DURATION)
  }, [clearTimers, logout])

  const extendSession = useCallback(() => {
    setSessionWarning(false)
    startSessionTimer()
  }, [startSessionTimer])

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api.auth.login(email, password)
      setUser(res.user)
      setToken(res.access_token)
      localStorage.setItem("cms_token", res.access_token)
      localStorage.setItem("cms_user", JSON.stringify(res.user))
      startSessionTimer()
      router.push("/dashboard")
    },
    [startSessionTimer, router]
  )

  const hasRole = useCallback(
    (role: UserRole | UserRole[]) => {
      if (!user) return false
      const roles = Array.isArray(role) ? role : [role]
      return roles.includes(user.role)
    },
    [user]
  )

  useEffect(() => {
    const savedToken = localStorage.getItem("cms_token")
    const savedUser = localStorage.getItem("cms_user")
    const savedExpiry = localStorage.getItem("cms_token_expiry")

    if (savedToken && savedUser && savedExpiry) {
      const expiry = parseInt(savedExpiry)
      if (Date.now() < expiry) {
        setToken(savedToken)
        setUser(JSON.parse(savedUser))
        expiryRef.current = expiry
        const remaining = expiry - Date.now()
        if (remaining <= WARNING_THRESHOLD) {
          setSessionWarning(true)
          setTimeRemaining(Math.ceil(remaining / 1000))
          countdownRef.current = setInterval(() => {
            const rem = Math.max(0, Math.ceil((expiryRef.current - Date.now()) / 1000))
            setTimeRemaining(rem)
            if (rem <= 0) logout()
          }, 1000)
        } else {
          startSessionTimer()
        }
      } else {
        localStorage.removeItem("cms_token")
        localStorage.removeItem("cms_user")
        localStorage.removeItem("cms_token_expiry")
      }
    }
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isEditor = user?.role === "editor" || user?.role === "manager"
  const isManager = user?.role === "manager"

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        isLoading,
        login,
        logout,
        hasRole,
        isEditor,
        isManager,
        sessionWarning,
        extendSession,
        timeRemaining,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
